import type { Job, KpiFilter, Worker, WorkerStatus } from '../types'
import { getActiveWorkerIds, isJobUnassigned, isJobUnderstaffed } from './job-workers'
import { diffDateKeysInDays, formatLateBy, formatOverdue, isValidDate, toDateKey } from './time'

export { isJobUnassigned, isJobUnderstaffed }

/** Minutes after the scheduled start before an assigned-but-not-started job counts as late. */
const LATE_GRACE_MINUTES = 5

export type JobDisplayKind = 'unassigned' | 'overdue' | 'scheduled' | 'en_route' | 'in_progress' | 'completed' | 'late'

export interface JobDisplayStatus {
  kind: JobDisplayKind
  label: string
  /** Secondary text such as "12 min late". */
  detail?: string
  overdueDays?: number
  /** Minutes late (late/unassigned-past-start), for compact chips like "Late 46m". */
  lateMinutes?: number
}

const lateMinutesOf = (start: number | null, now: number) => (start != null && now > start ? Math.floor((now - start) / 60_000) : undefined)

export function getJobDisplayStatus(job: Job, now: number, timeZone: string): JobDisplayStatus {
  if (job.status === 'completed') return { kind: 'completed', label: 'Completed' }
  if (job.status === 'in_progress') return { kind: 'in_progress', label: 'In progress' }

  // Assignments still loading: who is on it (and so unassigned / late) is not known yet.
  if (job.staffingPending) return { kind: 'scheduled', label: 'Loading…' }

  const start = isValidDate(job.scheduledStart) ? new Date(job.scheduledStart).getTime() : null

  if (isJobUnassigned(job)) {
    if (start != null) {
      const overdueDays = diffDateKeysInDays(toDateKey(now, timeZone), toDateKey(start, timeZone))
      if (overdueDays > 0) {
        return { kind: 'overdue', label: formatOverdue(overdueDays), overdueDays }
      }
    }
    return { kind: 'unassigned', label: 'Unassigned', detail: start != null && now > start ? formatLateBy(start, now) : undefined, lateMinutes: lateMinutesOf(start, now) }
  }

  const pastGrace = start != null && now > start + LATE_GRACE_MINUTES * 60_000
  if (job.status === 'late' || ((job.status === 'scheduled' || job.status === 'en_route') && pastGrace)) {
    return { kind: 'late', label: 'Late', detail: start != null ? formatLateBy(start, now) : undefined, lateMinutes: lateMinutesOf(start, now) }
  }

  if (job.status === 'en_route') return { kind: 'en_route', label: 'En route' }
  return { kind: 'scheduled', label: 'Scheduled' }
}

export function isJobLate(job: Job, now: number, timeZone: string): boolean {
  return getJobDisplayStatus(job, now, timeZone).kind === 'late'
}

export const WORKER_STATUS_LABEL: Record<WorkerStatus, string> = {
  available: 'Available',
  on_job: 'On job',
  late: 'Running late',
  offline: 'Offline',
}

export function isJobOnDate(job: Job, dateKey: string, timeZone: string): boolean {
  return isValidDate(job.scheduledStart) && toDateKey(job.scheduledStart, timeZone) === dateKey
}

export interface KpiCounts {
  workersOnJob: number
  totalWorkers: number
  availableNow: number
  jobsUnassigned: number
  runningLate: number
}

export function computeKpis(workers: Worker[], jobs: Job[], now: number, timeZone: string): KpiCounts {
  return {
    workersOnJob: workers.filter((worker) => worker.status === 'on_job').length,
    totalWorkers: workers.length,
    availableNow: workers.filter((worker) => worker.status === 'available').length,
    jobsUnassigned: jobs.filter((job) => isJobUnassigned(job, now)).length,
    runningLate: jobs.filter((job) => isJobLate(job, now, timeZone)).length,
  }
}

/**
 * Narrows workers and jobs to what a KPI card represents so the jobs panel, map and
 * timeline all show the same slice.
 */
export function applyKpiFilter(
  workers: Worker[],
  jobs: Job[],
  filter: KpiFilter | null,
  now: number,
  timeZone: string,
): { workers: Worker[]; jobs: Job[] } {
  if (!filter) return { workers, jobs }

  switch (filter) {
    case 'on_job': {
      const nextWorkers = workers.filter((worker) => worker.status === 'on_job')
      const ids = new Set(nextWorkers.map((worker) => worker.id))
      return {
        workers: nextWorkers,
        jobs: jobs.filter((job) => job.status !== 'completed' && getActiveWorkerIds(job).some((id) => ids.has(id))),
      }
    }
    case 'available':
    case 'unassigned':
      return {
        workers: workers.filter((worker) => worker.status === 'available'),
        jobs: jobs.filter((job) => isJobUnassigned(job)),
      }
    case 'late': {
      const lateJobs = jobs.filter((job) => isJobLate(job, now, timeZone))
      const ids = new Set(lateJobs.flatMap((job) => getActiveWorkerIds(job)))
      return {
        workers: workers.filter((worker) => worker.status === 'late' || ids.has(worker.id)),
        jobs: lateJobs,
      }
    }
  }
}

/** Jobs on the worker's schedule that overlap the candidate job's time window. */
export function findScheduleConflicts(candidate: Job, workerJobs: Job[]): Job[] {
  const start = new Date(candidate.scheduledStart).getTime()
  const end = new Date(candidate.scheduledEnd).getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end)) return []

  return workerJobs.filter((job) => {
    if (job.id === candidate.id || job.status === 'completed') return false
    const otherStart = new Date(job.scheduledStart).getTime()
    const otherEnd = new Date(job.scheduledEnd).getTime()
    return Number.isFinite(otherStart) && Number.isFinite(otherEnd) && otherStart < end && start < otherEnd
  })
}
